(() => {
  if (customElements.get('mz-performance-hero')) return;

  class MzPerformanceHero extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedOnce) return;
      this.isConnectedOnce = true;

      this.slides = Array.from(this.querySelectorAll('[data-mz-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-mz-hero-dot]'));
      this.videos = Array.from(this.querySelectorAll('[data-mz-hero-video]'));
      this.externalVideos = Array.from(this.querySelectorAll('[data-mz-hero-external]'));
      this.previousButton = this.querySelector('[data-mz-hero-previous]');
      this.nextButton = this.querySelector('[data-mz-hero-next]');
      this.motionButton = this.querySelector('[data-mz-hero-motion]');
      this.pagination = this.querySelector('[data-mz-hero-pagination]');
      this.status = this.querySelector('[data-mz-hero-status]');
      this.viewport = this.querySelector('[data-mz-hero-viewport]');

      this.autoplay = this.dataset.autoplay === 'true';
      this.videoAutoplay = this.dataset.videoAutoplay === 'true';
      this.pauseAfterInteraction = this.dataset.pauseAfterInteraction === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.enableSwipe = this.dataset.enableSwipe === 'true';
      this.speed = Math.max(1000, Number.parseInt(this.dataset.speed, 10) || 7000);
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.userPaused = false;
      this.motionOverride = false;
      this.interactionPlaybackOverride = false;
      this.isHovering = false;
      this.isFocusWithin = false;
      this.isInViewport = true;
      this.isDocumentVisible = !document.hidden;
      this.pointerStart = null;
      this.timer = null;
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = this.reducedMotionQuery.matches;

      this.bindEvents();
      this.observeVisibility();
      this.updatePosters();
      this.activate(this.currentIndex, { source: 'initial', announce: false, restartVideo: false });

      window.requestAnimationFrame(() => {
        this.classList.add('is-ready');
        this.refreshMotion();
      });
    }

    disconnectedCallback() {
      this.clearTimer();
      this.pauseAllVideos();
      this.pauseAllExternalVideos();
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      this.reducedMotionQuery?.removeEventListener('change', this.handleReducedMotionChange);
      this.mobileQuery?.removeEventListener('change', this.handleMobileChange);
      this.isConnectedOnce = false;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.navigate(-1, 'previous'), { signal });
      this.nextButton?.addEventListener('click', () => this.navigate(1, 'next'), { signal });
      this.motionButton?.addEventListener('click', () => this.toggleMotion(), { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = Number.parseInt(dot.dataset.slideIndex, 10);
            this.handleManualNavigation(index, 'pagination');
          },
          { signal }
        );
      });

      this.pagination?.addEventListener('keydown', (event) => this.handlePaginationKeydown(event), { signal });

      if (this.pauseOnHover) {
        this.addEventListener(
          'pointerenter',
          () => {
            this.isHovering = true;
            this.refreshMotion();
          },
          { signal }
        );
        this.addEventListener(
          'pointerleave',
          () => {
            this.isHovering = false;
            if (!this.isFocusWithin) this.interactionPlaybackOverride = false;
            this.refreshMotion();
          },
          { signal }
        );
        this.addEventListener(
          'focusin',
          () => {
            this.isFocusWithin = true;
            this.refreshMotion();
          },
          { signal }
        );
        this.addEventListener(
          'focusout',
          () => {
            window.requestAnimationFrame(() => {
              this.isFocusWithin = this.contains(document.activeElement);
              if (!this.isFocusWithin && !this.isHovering) this.interactionPlaybackOverride = false;
              this.refreshMotion();
            });
          },
          { signal }
        );
      }

      if (this.enableSwipe && this.viewport) {
        this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
        this.viewport.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
        this.viewport.addEventListener('pointercancel', () => this.resetPointer(), { signal });
      }

      document.addEventListener(
        'visibilitychange',
        () => {
          this.isDocumentVisible = !document.hidden;
          this.refreshMotion();
        },
        { signal }
      );

      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });

      this.handleReducedMotionChange = (event) => {
        this.reducedMotion = event.matches;
        if (!event.matches) this.motionOverride = false;
        this.updateMotionButton();
        this.refreshMotion();
      };
      this.handleMobileChange = () => {
        this.updatePosters();
        this.videos.forEach((video) => {
          video.pause();
          video.load();
        });
        this.refreshMotion();
      };

      this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
      this.mobileQuery.addEventListener('change', this.handleMobileChange);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = entry.intersectionRatio >= 0.25;
          this.refreshMotion();
        },
        { threshold: [0, 0.25, 0.6] }
      );
      this.intersectionObserver.observe(this);
    }

    handlePaginationKeydown(event) {
      const keyMap = {
        ArrowLeft: this.currentIndex - 1,
        ArrowRight: this.currentIndex + 1,
        Home: 0,
        End: this.slides.length - 1
      };

      if (!(event.key in keyMap)) return;
      event.preventDefault();
      const targetIndex = this.normalizeIndex(keyMap[event.key]);
      this.handleManualNavigation(targetIndex, 'keyboard');
      this.dots[targetIndex]?.focus();
    }

    handlePointerDown(event) {
      if (!['touch', 'pen'].includes(event.pointerType)) return;
      if (event.target.closest('a, button, input, select, textarea')) return;

      this.pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handlePointerUp(event) {
      if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      const threshold = Math.max(45, this.clientWidth * 0.12);
      this.resetPointer();

      if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      this.navigate(deltaX < 0 ? 1 : -1, 'swipe');
    }

    resetPointer() {
      if (this.pointerStart) {
        this.viewport?.releasePointerCapture?.(this.pointerStart.id);
      }
      this.pointerStart = null;
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const selectedSlide = event.target.closest('[data-mz-hero-slide]');
      if (!selectedSlide) return;

      const index = this.slides.indexOf(selectedSlide);
      if (index < 0) return;

      this.userPaused = true;
      this.interactionPlaybackOverride = false;
      this.activate(index, { source: 'editor', announce: false });
      this.updateMotionButton();
    }

    navigate(direction, source) {
      this.handleManualNavigation(this.currentIndex + direction, source);
    }

    handleManualNavigation(index, source) {
      if (this.pauseAfterInteraction) {
        this.userPaused = true;
        this.interactionPlaybackOverride = false;
      }
      this.activate(index, { source, announce: true });
      this.updateMotionButton();
    }

    activate(index, options = {}) {
      if (!this.slides.length) return;

      const nextIndex = this.normalizeIndex(index);
      const changed = nextIndex !== this.currentIndex;
      this.currentIndex = nextIndex;

      this.pauseAllVideos();
      this.pauseAllExternalVideos();

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === nextIndex;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (changed && options.restartVideo !== false) {
        const activeVideo = this.activeSlide?.querySelector('[data-mz-hero-video]');
        if (activeVideo) {
          try {
            activeVideo.currentTime = 0;
          } catch (_error) {
            // Some browsers prevent seeking until metadata is ready.
          }
        }
      }

      if (options.announce) this.announceCurrentSlide();
      this.refreshMotion();

      this.dispatchEvent(
        new CustomEvent('mz:hero-change', {
          bubbles: true,
          detail: { index: nextIndex, source: options.source || 'unknown' }
        })
      );
    }

    normalizeIndex(index) {
      const length = this.slides.length;
      if (!length) return 0;
      return ((index % length) + length) % length;
    }

    get activeSlide() {
      return this.slides[this.currentIndex];
    }

    toggleMotion() {
      const explicitlyPaused = this.userPaused || (this.reducedMotion && !this.motionOverride);

      if (explicitlyPaused) {
        this.motionOverride = true;
        this.userPaused = false;
        this.interactionPlaybackOverride = true;
      } else {
        this.userPaused = true;
        this.interactionPlaybackOverride = false;
      }

      this.updateMotionButton();
      this.refreshMotion();
    }

    updateMotionButton() {
      if (!this.motionButton) return;

      const explicitlyPaused = this.userPaused || (this.reducedMotion && !this.motionOverride);
      const pauseLabel = this.motionButton.dataset.pauseLabel || 'Automatische Wiedergabe pausieren';
      const playLabel = this.motionButton.dataset.playLabel || 'Automatische Wiedergabe fortsetzen';
      this.motionButton.setAttribute('aria-pressed', explicitlyPaused ? 'true' : 'false');
      this.motionButton.setAttribute('aria-label', explicitlyPaused ? playLabel : pauseLabel);
    }

    isMotionBlocked() {
      return (
        this.userPaused ||
        (this.reducedMotion && !this.motionOverride) ||
        this.designMode ||
        !this.isInViewport ||
        !this.isDocumentVisible ||
        (this.pauseOnHover && !this.interactionPlaybackOverride && (this.isHovering || this.isFocusWithin))
      );
    }

    refreshMotion() {
      this.clearTimer();
      this.pauseAllVideos();
      this.pauseAllExternalVideos();
      this.resetProgress();

      const blocked = this.isMotionBlocked();
      if (!blocked && this.videoAutoplay) {
        this.playActiveVideo();
        this.playActiveExternalVideo();
      }

      if (!blocked && this.autoplay && this.slides.length > 1) {
        this.startProgress();
        this.timer = window.setTimeout(() => {
          this.activate(this.currentIndex + 1, { source: 'autoplay', announce: false });
        }, this.speed);
      }

      this.updateMotionButton();
    }

    clearTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    pauseAllVideos() {
      this.videos.forEach((video) => video.pause());
    }

    playActiveVideo() {
      const video = this.activeSlide?.querySelector('[data-mz-hero-video]');
      if (!video) return;

      video.preload = 'metadata';
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    pauseAllExternalVideos() {
      this.externalVideos.forEach((frame) => {
        if (!frame.contentWindow) return;
        const provider = frame.dataset.provider;
        const payload = provider === 'vimeo'
          ? { method: 'pause' }
          : { event: 'command', func: 'pauseVideo', args: [] };
        frame.contentWindow.postMessage(JSON.stringify(payload), '*');
      });
    }

    playActiveExternalVideo() {
      const frame = this.activeSlide?.querySelector('[data-mz-hero-external]');
      if (!frame) return;

      if (!frame.src) {
        frame.src = frame.dataset.src || '';
        return;
      }

      const provider = frame.dataset.provider;
      const payload = provider === 'vimeo'
        ? { method: 'play' }
        : { event: 'command', func: 'playVideo', args: [] };
      frame.contentWindow?.postMessage(JSON.stringify(payload), '*');
    }

    updatePosters() {
      const useMobilePoster = this.mobileQuery.matches;
      this.videos.forEach((video) => {
        const poster = useMobilePoster ? video.dataset.mobilePoster : video.dataset.desktopPoster;
        if (poster && video.getAttribute('poster') !== poster) video.setAttribute('poster', poster);
      });
    }

    resetProgress() {
      this.dots.forEach((dot) => dot.classList.remove('is-progressing'));
    }

    startProgress() {
      const activeDot = this.dots[this.currentIndex];
      if (!activeDot) return;

      activeDot.classList.remove('is-progressing');
      void activeDot.offsetWidth;
      activeDot.classList.add('is-progressing');
    }

    announceCurrentSlide() {
      if (!this.status) return;

      const heading = this.activeSlide?.querySelector('.mz-performance-hero__heading')?.textContent?.trim();
      this.status.textContent = `Folie ${this.currentIndex + 1} von ${this.slides.length}${heading ? `: ${heading}` : ''}`;
    }
  }

  customElements.define('mz-performance-hero', MzPerformanceHero);
})();
